import React from 'react'
import { useParams } from 'react-router-dom'
import {
  usePRFileArtboards,
  PRMarinaFileArtboard,
} from '@sketch-hq/sketch-web-renderer'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'
import { RouteParams, routes } from '@sketch/modules-common'

import {
  Trigger,
  CurrentFrame,
  Chevrons,
  Container,
} from './FrameSelector.styles'
import * as S from './FrameList/FrameList.styles'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

interface FrameListForPreviewProps {
  previewID: string
  pageUUID: string
  currentFrameUUID: string
  frames: PRMarinaFileArtboard[]
}

function FrameListForPreview({
  previewID,
  pageUUID,
  currentFrameUUID,
  frames,
}: FrameListForPreviewProps) {
  return (
    <>
      {frames.map(frame => {
        const pathname = routes.PREVIEW_FRAME_VIEW.create({
          previewID,
          pageUUID,
          frameUUID: frame.objectId,
        })

        return (
          <S.StyledDropdownNavItem
            key={frame.objectId}
            data-testid="frame-selector-item"
            to={pathname}
            isActive={match =>
              Boolean(match) || frame.objectId === currentFrameUUID
            }
            exact
          >
            <SFSymbolLabel text={frame.name} />
          </S.StyledDropdownNavItem>
        )
      })}
    </>
  )
}

export function FrameSelectorForPreview() {
  const { previewID, pageUUID, frameUUID } =
    useParams<Partial<RouteParams<'PREVIEW_FRAME_VIEW'>>>()

  const frames = usePRFileArtboards() ?? []
  const currentFrame = frames.find(f => f.objectId === frameUUID)
  const isMobile = !useBreakpoint('sm')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: FrameListForPreview,
    dropdownProps: {
      previewID: previewID ?? '',
      pageUUID: pageUUID ?? '',
      currentFrameUUID: frameUUID ?? '',
      frames,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13],
  })

  const isDisabled = frames.length <= 1

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas frames list"
        data-testid="frames-selector-trigger"
        disabled={isDisabled}
      >
        <CurrentFrame>
          {currentFrame ? (
            <SFSymbolLabel text={currentFrame.name} />
          ) : (
            'Select frame...'
          )}
        </CurrentFrame>
        {!isDisabled && <Chevrons />}
      </Trigger>
      {!isDisabled && content}
    </Container>
  )
}
